/**
 * Visszahívások — handover rev7 §11.7 and §5.7: "Call, build the order in the
 * staff editor, send for payment, close."
 *
 * Three of those four are here: the list to call from, the link that opens
 * the staff editor on the basket stored with the request (§11.11.2,
 * *Callback conversion*, built by `041_staff_editor`), and closing a request
 * with what was said. Sending for payment is an outcome inside the editor.
 * Lapsing a request is the sweep's (§19.4, U18) and not a person's.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asMoment, Loading, useConsoleAction, useConsoleData } from '../common.tsx'

interface Callback {
  id: string
  name: string | null
  phone: string | null
  email: string | null
  note: string | null
  staff_note: string | null
  status: string
  expires_at: string | null
  created_at: string
  closed_at: string | null
  closed_by: { name?: string } | null
  lapsing: boolean
  event_params: Record<string, unknown>
}

export function Callbacks(): JSX.Element {
  const [status, setStatus] = useState('open')
  const { data, error, loading, reload } = useConsoleData<{ callbacks: Callback[] }>(
    'callbacks',
    { status },
  )
  const { run, busy, error: actionError } = useConsoleAction()
  const [note, setNote] = useState<Record<string, string>>({})

  async function close(callback: Callback): Promise<void> {
    if (await run('callback_close', { callback_id: callback.id, note: note[callback.id] ?? '' })) {
      setNote((previous) => ({ ...previous, [callback.id]: '' }))
      reload()
    }
  }

  return (
    <section data-testid="view-callbacks">
      <h2>Visszahívások</h2>
      <p className="lead">
        §5.7: a visszahívással még nincs lefoglalva semmi, és a választott nap időközben betelhet.
      </p>

      <label htmlFor="cb-status">Állapot</label>
      <select
        id="cb-status"
        value={status}
        onChange={(event) => setStatus(event.target.value)}
        data-testid="callback-status"
      >
        <option value="open">nyitott</option>
        <option value="converted">rendeléssé vált</option>
        <option value="lapsed">lejárt</option>
        <option value="closed">lezárt</option>
      </select>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && data.callbacks.length === 0 && (
        <p className="note" data-testid="callbacks-empty">Nincs ilyen állapotú visszahívás.</p>
      )}

      {data?.callbacks.map((callback) => (
        <article className="card" key={callback.id} data-testid={`callback-${callback.id}`}>
          <h3>
            {callback.name ?? 'Név nélkül'} · {callback.phone ?? '—'}
            {callback.lapsing && <span className="tag warn">hamarosan lejár</span>}
          </h3>
          <dl className="facts">
            <dt>Beérkezett</dt>
            <dd>{asMoment(callback.created_at)}</dd>
            <dt>Lejár</dt>
            <dd>{asMoment(callback.expires_at)}</dd>
            <dt>E-mail</dt>
            <dd>{callback.email ?? '—'}</dd>
            <dt>Megjegyzés</dt>
            <dd>{callback.note ?? '—'}</dd>
          </dl>

          {callback.status === 'open'
            ? (
              <>
                <label htmlFor={`cbnote-${callback.id}`}>Mi hangzott el a hívásban?</label>
                <textarea
                  id={`cbnote-${callback.id}`}
                  rows={2}
                  value={note[callback.id] ?? ''}
                  onChange={(event) =>
                    setNote((previous) => ({ ...previous, [callback.id]: event.target.value }))}
                  data-testid={`callback-note-${callback.id}`}
                />
                <div className="actions">
                  <Link
                    className="tag"
                    to={`/staff/editor?callback=${callback.id}`}
                    data-testid={`callback-editor-${callback.id}`}
                  >
                    Rendelés összeállítása
                  </Link>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void close(callback)}
                    data-testid={`callback-close-${callback.id}`}
                  >
                    Lezárom
                  </button>
                </div>
                <p className="note">
                  §11.11.2: a rendelés összeállítása a tárolt kosárból a rendelésszerkesztőben
                  történik.
                </p>
              </>
            )
            : (
              <p className="note">
                {callback.status} · {asMoment(callback.closed_at)}
                {callback.closed_by?.name ? ` · ${callback.closed_by.name}` : ''}
                {callback.staff_note !== null ? ` · ${callback.staff_note}` : ''}
              </p>
            )}
        </article>
      ))}
    </section>
  )
}
